import React from 'react';
import { cn } from '@/utils/cn';
import { motion } from 'framer-motion';
import SeoMeta from '@/components/seo/SeoMeta';
import { about, templateData } from '@/data/content';
import Button from '@/components/ui/Button';

interface AboutSection {
    title: string;
    items: string[];
}

interface AboutData {
    body: string;
    values: string[];
    philosophy: AboutSection;
    philosophy_heading: string;
    hero_image?: string;
    hero_badge?: string;
    images?: { src: string; alt: string }[];
    differentiators: AboutSection;
}

const FALLBACK: AboutData = about as unknown as AboutData;

const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 },
};

const AboutPage: React.FC = () => {
    const data = FALLBACK;

    const paragraphs = data.body.split(/\n\n+/).filter(Boolean);

    const heroSection = (
        <motion.div
            {...fadeInUp}
            className="relative bg-white dark:bg-gray-800 rounded-[2.5rem] md:rounded-[3.5rem] border border-app-border dark:border-gray-700 overflow-hidden mb-12 shadow-sm"
        >
            {/* Background Image Overlay */}
            <div className="absolute inset-0 z-0 opacity-10 dark:opacity-20 pointer-events-none">
                {data.hero_image && <img src={data.hero_image} alt="" className="w-full h-full object-cover" />}
                <div className="absolute inset-0 bg-gradient-to-r from-white via-white/40 to-transparent dark:from-gray-800 dark:via-gray-800/40"></div>
            </div>

            <div className="relative z-10 p-8 md:p-16">
                <div className="max-w-3xl text-left">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-app-primary/10 text-app-primary text-[10px] md:text-xs font-black uppercase tracking-[0.2em] mb-6">
                        <span className="w-1.5 h-1.5 rounded-full bg-app-primary"></span>
                        {data.hero_badge}
                    </div>
                    <h1 className="text-4xl md:text-6xl font-black text-app-text dark:text-white mb-6 tracking-tight">
                        {about.page_heading}
                    </h1>
                    <p className="text-lg md:text-xl text-app-muted dark:text-gray-300 font-medium leading-relaxed">
                        {data.philosophy_heading}
                    </p>
                </div>
            </div>
        </motion.div>
    );

    return (
        <>
            <SeoMeta page="about-us" />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-16 pb-8 md:pb-12">
            <div className="mb-6 flex justify-start">
                <Button to="/" size="sm" variant="outline" className="px-6 py-2">
                    ← {templateData.back_home}
                </Button>
            </div>
            {heroSection}

            {/* ── Mobile Layout ── */}
            <div className="md:hidden space-y-10">
                <div className="space-y-6">
                    {paragraphs.map((para, i) => (
                        <React.Fragment key={i}>
                            <motion.p
                                {...fadeInUp}
                                transition={{ delay: i * 0.1 }}
                                className="text-app-muted dark:text-gray-300 leading-[1.8] whitespace-pre-line text-base"
                            >
                                {para}
                            </motion.p>
                            {i === 1 && data.images?.[0] && (
                                <motion.div
                                    {...fadeInUp}
                                    className="my-10 rounded-[2.5rem] overflow-hidden shadow-lg border border-app-border dark:border-gray-700"
                                >
                                    <img
                                        src={data.images[0].src}
                                        alt={data.images[0].alt}
                                        className="w-full aspect-[4/3] object-cover"
                                    />
                                </motion.div>
                            )}
                        </React.Fragment>
                    ))}
                </div>

                {data.values.length > 0 && (
                    <motion.div
                        {...fadeInUp}
                        className="bg-app-primary/10 dark:bg-app-primary/20 rounded-[2rem] p-8 border-2 border-app-primary/20 shadow-lg shadow-app-primary/5"
                    >
                        <p className="text-[10px] font-black text-app-primary uppercase tracking-[0.2em] mb-6">
                            {about.values_label}
                        </p>
                        <ul className="space-y-4">
                            {data.values.map((v, i) => (
                                <li key={i} className="flex items-center gap-4 text-app-text dark:text-white font-bold">
                                    <span className="w-2 h-2 rounded-full bg-app-primary flex-shrink-0" />
                                    {v}
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                )}

                <div className="space-y-6">
                    {[data.philosophy, data.differentiators].map((section, idx) => (
                        <motion.div
                            key={section.title}
                            {...fadeInUp}
                            transition={{ delay: 0.2 + idx * 0.1 }}
                            className={cn(
                                'border rounded-[2rem] p-8 shadow-sm',
                                idx === 1
                                    ? 'bg-app-primary/5 border-app-primary/10 dark:bg-app-primary/5 dark:border-app-primary/10'
                                    : 'bg-white dark:bg-gray-800 border-app-border dark:border-gray-700'
                            )}
                        >
                            <h3
                                className={cn('text-xl font-black mb-4', idx === 1 ? 'text-app-primary' : 'text-app-text dark:text-white')}
                            >
                                {section.title}
                            </h3>
                            <ul className="space-y-4">
                                {section.items.map((item, i) => (
                                    <li key={i} className="flex items-start gap-3 text-app-muted dark:text-gray-300">
                                        <div className="w-5 h-5 rounded-full bg-app-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                                            <svg
                                                className="w-3.5 h-3.5 text-app-primary"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                        </div>
                                        <span className="text-sm font-medium">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* ── Desktop Layout ── */}
            <div className="hidden md:block">
                <div className="grid lg:grid-cols-5 gap-16 items-start">
                    {/* Left: body text + values */}
                    <div className="lg:col-span-3 space-y-10">
                        {paragraphs.map((para, i) => (
                            <React.Fragment key={i}>
                                <motion.p
                                    {...fadeInUp}
                                    transition={{ delay: i * 0.1 }}
                                    className="text-lg text-app-muted dark:text-gray-300 leading-[1.8] whitespace-pre-line"
                                >
                                    {para}
                                </motion.p>
                                {i === 1 && data.images?.[0] && (
                                    <motion.div
                                        {...fadeInUp}
                                        className="my-12 rounded-[3rem] overflow-hidden shadow-xl border border-app-border dark:border-gray-700"
                                    >
                                        <img
                                            src={data.images[0].src}
                                            alt={data.images[0].alt}
                                            className="w-full aspect-video object-cover"
                                        />
                                    </motion.div>
                                )}
                            </React.Fragment>
                        ))}

                        {data.values.length > 0 && (
                            <motion.div
                                {...fadeInUp}
                                className="mt-12 bg-app-primary/10 dark:bg-app-primary/20 rounded-[2.5rem] p-10 border-2 border-app-primary/20 shadow-xl shadow-app-primary/5"
                            >
                                <p className="text-xs font-black text-app-primary uppercase tracking-[0.2em] mb-8">
                                    {about.values_label}
                                </p>
                                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    {data.values.map((v, i) => (
                                        <li
                                            key={i}
                                            className="flex items-center gap-4 text-app-text dark:text-white text-xl font-black"
                                        >
                                            <span className="w-3 h-3 rounded-full bg-app-primary flex-shrink-0 shadow-lg shadow-app-primary/40" />
                                            {v}
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        )}
                    </div>

                    {/* Right: philosophy + differentiators */}
                    <div className="lg:col-span-2 space-y-8">
                        {[data.philosophy, data.differentiators].map((section, idx) => (
                            <motion.div
                                key={section.title}
                                {...fadeInUp}
                                transition={{ delay: 0.3 + idx * 0.1 }}
                                className={cn(
                                    'border rounded-[2.5rem] p-10 shadow-sm transition-all duration-500 group hover:-translate-y-1',
                                    idx === 1
                                        ? 'bg-app-primary/5 border-app-primary/10 dark:bg-app-primary/5 dark:border-app-primary/10 hover:shadow-xl hover:shadow-app-primary/5'
                                        : 'bg-white dark:bg-gray-800 border-app-border dark:border-gray-700 hover:shadow-xl'
                                )}
                            >
                                <h3
                                    className={cn('text-2xl font-black mb-6 group-hover:text-app-primary transition-colors', idx === 1 ? 'text-app-primary' : 'text-app-text dark:text-white')}
                                >
                                    {section.title}
                                </h3>
                                <ul className="space-y-5">
                                    {section.items.map((item, i) => (
                                        <li
                                            key={i}
                                            className="flex items-start gap-4 text-app-muted dark:text-gray-300"
                                        >
                                            <div className="w-6 h-6 rounded-full bg-app-primary/10 text-app-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                            </div>
                                            <span className="text-base font-medium">{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

export default AboutPage;
